import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import GlobalCartSyncService from "../services/GlobalCartSyncService";
import { useCartOperationStatus } from "./useCartOperationStatus";

/**
 * Hook to monitor cart location status
 * Replaces useCartLocationSync with a more lightweight approach that uses GlobalCartSyncService
 */
export function useCartLocationStatus() {
  const [status, setStatus] = useState<string>("idle");
  const [locationUpdateJustCompleted, setLocationUpdateJustCompleted] =
    useState(false);
  const prevStatus = useRef<string>("idle");

  // Get location from Redux for current value
  const location = useSelector((state: any) => state.location);

  // Use existing hook for operation status (to avoid duplicate subscriptions)
  const { isLoading, operationType } = useCartOperationStatus();

  // Subscribe to status changes from GlobalCartSyncService
  useEffect(() => {
    const handleStatusChange = (newStatus: string) => {
      setStatus(newStatus);

      // Set the just completed flag when status changes from updating to complete
      if (prevStatus.current === "updating" && newStatus === "complete") {
        setLocationUpdateJustCompleted(true);

        // Reset flag after a short delay (increasing from 300ms to 500ms to ensure components have time to react)
        setTimeout(() => {
          setLocationUpdateJustCompleted(false);
        }, 500);
      }

      prevStatus.current = newStatus;
    };

    const unsubscribe =
      GlobalCartSyncService.onStatusChange(handleStatusChange);
    return unsubscribe;
  }, []);

  // Determine if location is being updated
  const isLocationUpdating =
    (isLoading && operationType === "LOCATION_UPDATE") || status === "updating";

  return {
    isLocationUpdating,
    locationStatus: status,
    currentZoneId: location?.deliveryFeeZone?.documentId || null,
    province: location?.province,
    address: location?.address,
    locationUpdateJustCompleted, // Exposed flag for tracking just-completed updates
  };
}

export default useCartLocationStatus;
